import { useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';

import { Portal, usePreventWindowScroll } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useSnackbar } from '@/features/snackbar';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import { Sentry } from '@/components/Sentry';

import { useIsMobileScreen } from '@/hooks/useMedia';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import ActionSheet, { Background, Sheet } from '@/components/ActionSheet';

import { TransactionForm } from './components/TransactionForm';
import {
  getTransactionFromFieldValues,
  useAddTransactionToPortfolio,
  useTransactionForm,
} from './hooks';

const sheetWidth = 700;

type Props = {
  holding?: Holding;
  portfolioId: Portfolio['id'];
  onClose: () => void;
};

export const AddTransaction = ({ onClose, portfolioId, holding }: Props) => {
  const [open, setOpen] = useState(true);
  const [savedTicker, setSavedTicker] = useState<string | undefined>();

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { trackEvent } = useTracking({
    subject: 'addTransactionModal',
    portfolioId,
  });

  const { addSnackbar } = useSnackbar();

  usePreventWindowScroll();

  const mutationState = useAddTransactionToPortfolio();
  const { mutate } = mutationState;

  const formState = useTransactionForm({
    initialValues: {
      type: 'BUY',
      ticker:
        typeof holding === 'undefined'
          ? null
          : {
              symbol: holding.symbol,
              companyName: holding.company.name,
              exchangeCode: holding.company.exchangeSymbol,
              currencyISO: getTradingItemCurrencyISO(
                holding.company.data.currencyInfo
              ),
            },
    },
  });

  const handleClose = useCallback(() => {
    trackEvent({ modifier: 'closeIcon', action: 'click' });
    setOpen(false);
    onClose();
  }, [onClose, trackEvent]);

  const handleSave = useCallback(() => {
    trackEvent({ modifier: 'save', action: 'click' });
    const isValid = formState.validate();

    if (isValid === false) return;

    const transaction = getTransactionFromFieldValues(formState.values);

    if (transaction === null) return;

    mutate(
      {
        id: portfolioId,
        transaction,
        triggerRecalculation: true,
      },
      {
        onSuccess() {
          onClose();
          addSnackbar({
            type: 'positive',
            message: 'All transactions saved successfully',
            lifeTime: 2000,
          });
        },
      }
    );
  }, [trackEvent, formState, mutate, portfolioId, onClose, addSnackbar]);

  const handleSaveAndAddMore = useCallback(() => {
    trackEvent({ modifier: 'saveAndAddMore', action: 'click' });
    const isValid = formState.validate();

    if (isValid === false) return;

    const transaction = getTransactionFromFieldValues(formState.values);

    if (transaction === null) return;

    mutate(
      {
        id: portfolioId,
        transaction,
        triggerRecalculation: true,
      },
      {
        onSuccess() {
          setSavedTicker(`${transaction.exchangeCode}:${transaction.symbol}`);
          formState.reset();
        },
      }
    );
  }, [trackEvent, formState, mutate, portfolioId]);

  return (
    <Sentry
      sentryCapture={{
        team: 'wombats',
      }}
      globalDisabled
    >
      {isMobileScreen ? (
        <Portal>
          <div
            className="fixed left-0 top-0 z-[9999] size-full bg-legacy-noir"
            data-testid="add-transaction-modal"
          >
            <div className="pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
              <div className="grid h-[calc(100svh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] grid-rows-[1fr]">
                <TransactionForm
                  holding={holding}
                  formState={formState}
                  mutationState={mutationState}
                  savedTicker={savedTicker}
                  onClose={handleClose}
                  onSave={handleSave}
                  onSaveAndAddMore={handleSaveAndAddMore}
                />
              </div>
            </div>
          </div>
        </Portal>
      ) : (
        <ActionSheet
          open={open}
          onClose={() => setOpen(false)}
          onClosed={onClose}
        >
          <Background />
          <Sheet
            sheetLayout={{
              'max-width': `${sheetWidth}px`,
              left: `max(0px, calc(50vw - ${sheetWidth / 2}px))`,
            }}
          >
            <div
              className="grid grid-rows-[calc(50px+env(safe-area-inset-top))]"
              data-testid="add-transaction-action-sheet"
            >
              <div className="row-start-2 grid rounded-t-md bg-legacy-noir">
                <TransactionForm
                  holding={holding}
                  formState={formState}
                  mutationState={mutationState}
                  savedTicker={savedTicker}
                  onClose={handleClose}
                  onSave={handleSave}
                  onSaveAndAddMore={handleSaveAndAddMore}
                />
              </div>
            </div>
          </Sheet>
        </ActionSheet>
      )}
    </Sentry>
  );
};
